import { Injectable, EventEmitter } from '@angular/core';
import { BehaviorSubject, noop, Subject } from 'rxjs';
import { jwtDecode } from 'jwt-decode';

import { IntermediaryService, MS_DELAY } from '#services/intermediary/intermediary.service';
import { AuthUserResponse, PublicUser, OAuthUserResponse } from 'src/app/models/db/user/user.model';
import { OAuthSignInRequest, SignInRequest } from '#models/db/user/sign-in-request.model';
import { OAuthSignUpRequest, SignUpRequest } from '#models/db/user/sign-up-request.model';
import { AuthRepositoryService } from 'src/app/repositories/auth-repository/auth-repository.service';
import { LyfePlumJwtPayload } from '#models/db/request/request-options-base.model';
import { DesignatedContactPermission } from '#models/db/profile/profile.model';
import { logger } from '#lib/logger';
import { User } from '@supabase/supabase-js';
import { DesignatedContactInfo } from '#models/db/designated-contact/designated-contact.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  /**
   * Used for email/password authentication loading state.
   */
  authLoading$ = new Subject<boolean>();
  /**
   * Used for OAuth loading state.
   */
  oauthLoading$ = new Subject<boolean>();
  creatingOAuthUserLoading$ = new Subject<boolean>();
  signUpLoading$ = new Subject<boolean>();
  logoutLoading$ = new Subject<boolean>();
  resetPasswordLoading$ = new Subject<boolean>();
  updatePasswordLoading$ = new Subject<boolean>();

  /**
   * User associated with the current session.
   */
  activeUser$ = new Subject<PublicUser | null>();
  authUserResponse$ = new Subject<AuthUserResponse | null>();
  oauthUserResponse$ = new Subject<OAuthUserResponse | null>();

  permissions$ = new BehaviorSubject<DesignatedContactPermission[] | null>(null);
  /**
   * The currently active designated contact permission.
   * This is set when switching profiles when the user is a designated contact.
   */
  activePermission$ = new BehaviorSubject<DesignatedContactPermission | null>(null);

  /**
   * Emits when a logout event occurs.
   */
  logoutEvent = new EventEmitter<void>();

  private _accessToken: string | null = null;
  private _provider = '';

  get accessToken(): string | null {
    return this._accessToken;
  }

  /**
   * The auth provider used for the current session.
   */
  get provider(): string {
    return this._provider;
  }

  /**
   * Gets the current value of the designated contact permission.
   *
   * @remarks
   * Don't use this for the UI, use the `activePermission$` observable instead.
   * This method is primarily for internal logic checks.
   *
   * @returns The current {@link DesignatedContactPermission} if set, otherwise `null`.
   */
  get activePermission(): DesignatedContactPermission | null {
    return this.activePermission$.value;
  }

  /**
   * Indicates whether the current user has sufficient permissions to create or edit.
   *
   * If the user has no active permission, they are considered not a designated contact
   * and are granted permission to create and edit by default. Otherwise, the user must
   * have the 'Edit' permission to be granted access.
   *
   * @returns `true` if the user has no active permission or has 'Edit' permission; otherwise, `false`.
   */
  get hasAppropriatePermissions(): boolean {
    if (!this.activePermission) {
      // No permissions means the user is not a designated contact, so they can create and edit.
      return true;
    }

    // Check if the user has 'Edit' permission.
    return this.activePermission.permission === 'Edit';
  }

  get designatedContactRedirectUrl(): string {
    return this.authRepository.designatedContactsRedirectTo;
  }

  constructor(
    private authRepository: AuthRepositoryService,
    private intermediaryHandler: IntermediaryService
  ) {}

  /**
   * Checks if there is a valid access token in the session.
   *
   * @param {boolean} [verifyUser=false] - Optional flag to check if the user is valid.
   *
   * @returns {Promise<boolean>} A promise that resolves to `true` if a valid access token exists, otherwise `false`.
   */
  async hasValidAccessToken(verifyUser?: boolean): Promise<boolean> {
    const session = await this.authRepository.getSession();

    if (session) {
      this._accessToken = session.access_token;
      this._provider = session.user.app_metadata.provider ?? '';

      if (verifyUser) {
        const userId = await this.getUser();

        if (!userId) {
          this.signOut(true, noop, true);
          return false;
        }
      }

      return true;
    }

    this._accessToken = null;
    this._provider = '';

    return false;
  }

  /**
   * Sets the session based on the OAuth redirect URL.
   *
   * @param {string} redirectTo - The URL to redirect to after setting the session.
   * @returns {Promise<boolean>} - A promise that resolves to `true` if the session was successfully set, otherwise `false`.
   */
  async setSession(redirectTo: string): Promise<boolean> {
    const session = await this.authRepository.setSessionOnOAuthRedirect(redirectTo);

    if (session) {
      return true;
    }

    return false;
  }

  /**
   * Retrieves the public user ID from the current access token.
   *
   * @description
   * Decodes the JWT access token and extracts the `public_user_id` property from its payload.
   * If a designated contact permission is active, it returns the `requester_user_id` from that permission instead.
   *
   * @param {boolean} ignorePermissionCheck - If `true`, ignores the active permission check and returns the public user ID directly from the access token.
   *
   * @returns {string} The public user ID if available; otherwise, an empty string.
   */
  getPublicUserId(ignorePermissionCheck?: boolean): string {
    const jwt = this.accessToken;

    if (!jwt) {
      return '';
    }

    const decoded = jwtDecode<LyfePlumJwtPayload>(jwt);
    let public_user_id = decoded.public_user_id;

    if (this.activePermission && !ignorePermissionCheck) {
      public_user_id = this.activePermission.requester_user_id;
    }

    return public_user_id;
  }

  /**
   * Gets the current user details if there is an existing session.
   *
   * @returns A promise that resolves to the current {@link User} object if authenticated, or `null`
   * if no user is signed in.
   */
  async getUser(): Promise<User | null> {
    return this.authRepository.getUser();
  }

  /**
   * Signs up a user with an email.
   *
   * - {@link authLoading$}
   * - {@link activeUser$}
   *
   * @param {SignUpRequest} request - The sign-up request containing user details.
   * @param {(data: PublicUser | null) => void} [nextFn] - Optional callback function to be executed with the user data or null.
   */
  signUpWithEmail(request: SignUpRequest, nextFn?: (data: PublicUser | null) => void) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.signUpLoading$,
        subject: this.activeUser$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        return await this.authRepository.signUpWithEmail(request);
      }
    );
  }

  /**
   * Signs in/up a user using OAuth.
   *
   * - {@link oauthLoading$}
   * - {@link oauthUserResponse$}
   *
   * @param {OAuthSignInRequest} request - The OAuth sign-in request containing necessary credentials.
   * @param {(data: OAuthUserResponse | null) => void} [nextFn] - Optional callback function to handle the response data.
   */
  signInWithOAuth(request: OAuthSignInRequest, nextFn?: (data: OAuthUserResponse | null) => void) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.oauthLoading$,
        subject: this.oauthUserResponse$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        return await this.authRepository.signInWithOAuth(request);
      }
    );
  }

  /**
   * Signs in a user using the provided request data.
   *
   * - {@link authLoading$}
   * - {@link authUserResponse$}
   *
   * @param {SignInRequest} request - The request data for signing in.
   * @param {(data: AuthUserResponse | null) => void} [nextFn] - Optional callback function to be executed with the authentication result.
   */
  signIn(request: SignInRequest, nextFn?: (data: AuthUserResponse | null) => void) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.authLoading$,
        subject: this.authUserResponse$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        return await this.authRepository.signIn(request);
      }
    );
  }

  /**
   * Signs the user out of the application.
   *
   * Sets the following:
   * * {@link logoutLoading$}
   *
   * @param global - A boolean indicating whether to sign out globally.
   * @param nextFn - An optional callback function to be executed after sign out.
   *
   * @event {@link logoutEvent} - Emits when the sign-out process is complete.
   */
  signOut(global: boolean, nextFn?: () => void, silent?: boolean) {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.logoutLoading$ : undefined,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        await this.authRepository.signOut(global);

        this.resetAuth();
        // Emit logout event.
        this.logoutEvent.emit();
      }
    );
  }

  /**
   * Creates a new OAuth user using the provided sign-up request.
   *
   * Sets the following:
   * * {@link creatingOAuthUserLoading$}
   *
   * @param {OAuthSignUpRequest} request - The sign-up request containing provider information.
   * @param {() => void} [nextFn] - Optional callback function to be executed after the user is created.
   */
  createOAuthUser(request: OAuthSignUpRequest, nextFn?: () => void) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.creatingOAuthUserLoading$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        await this.authRepository.createOAuthUser(request);
      }
    );
  }

  /**
   * Initiates the password reset process for the specified email address.
   *
   * Sets the following:
   * * {@link resetPasswordLoading$}
   *
   * @param email - The email address for which to reset the password.
   * @param nextFn - (Optional) A callback function to execute after the reset process completes.
   *
   * @remarks
   * This method will be used for Forgot Password and Change Password pages.
   */
  resetPassword(email: string, nextFn?: () => void) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.resetPasswordLoading$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        return await this.authRepository.resetPassword(email);
      }
    );
  }

  /**
   * Changes the user's password to the provided new password.
   *
   * Sets the following:
   * * {@link updatePasswordLoading$}
   *
   * @param newPassword - The new password to set for the user.
   * @param nextFn - (Optional) A callback function to execute after the password change operation completes.
   *
   * @remarks
   * Only use this method if the user is already authenticated and you want to change their password.
   * @see {@link resetPassword} for initiating a password reset process.
   */
  updatePassword(newPassword: string, nextFn?: (data: User | null) => void) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.updatePasswordLoading$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        return await this.authRepository.updatePassword(newPassword);
      }
    );
  }

  /**
   * Signs up a user using a designated contact invite.
   *
   * Sets the following:
   * * {@link signUpLoading$}
   * * {@link activeUser$}
   *
   * @param request - The sign-up request payload containing user information.
   * @param nextFn - Optional callback function to be executed with the resulting `PublicUser` or `null`.
   */
  signUpFromDesignatedContactInvite(
    request: SignUpRequest,
    nextFn?: (data: PublicUser | null) => void
  ) {
    this.intermediaryHandler.intermediary(
      {
        loading: this.signUpLoading$,
        subject: this.activeUser$,
        delay: MS_DELAY,
        nextFn,
      },
      async () => {
        return await this.authRepository.createUserFromDesignatedContactInvite(request);
      }
    );
  }

  /**
   * Extracts the recipient's designated contact information from a given access token.
   *
   * Decodes the provided JWT access token and retrieves the `designated_contact_info`
   * property from its payload. Returns `null` if the property is not present.
   *
   * @param access_token - The JWT access token containing the designated contact info.
   * @returns The extracted `DesignatedContactInfo` object if available; otherwise, `null`.
   */
  getRecipientDesignatedContactInfoFromAccessToken(
    access_token: string
  ): DesignatedContactInfo | null {
    const decoded = jwtDecode<LyfePlumJwtPayload>(access_token);

    logger.log(decoded, 'getRecipientDesignatedContactInfoFromAccessToken -> decoded');

    return decoded?.user_metadata?.designated_contact_info ?? null;
  }

  /**
   * Sets the user's designated contact permissions.
   *
   * Sets the following:
   * * {@link permissions$}
   *
   * @param permissions
   */
  setPermissions(permissions: DesignatedContactPermission[] | null) {
    this.permissions$.next(permissions);
  }

  /**
   * Updates the current designated contact permission.
   * This method is used to set the active permission for the user selected when switching profiles.
   *
   * Sets the following:
   * * {@link activePermission$}
   *
   * @param permission - The new designated contact permission to set, or `null` to clear the permission.
   */
  setDesignatedContactActivePermission(permission: DesignatedContactPermission | null) {
    this.activePermission$.next(permission);
  }

  resetAuth() {
    this.permissions$.next(null);
    this.activePermission$.next(null);
    logger.log('complete', 'AuthService.resetAuth');
  }
}
